import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import StatsCards from './StatsCards';
import EntriesTable from './EntriesTable';
import FilterBar from './FilterBar';

const { FiPlus, FiInbox } = FiIcons;

function Dashboard({ entries, onUpdateEntry, onUpdateCompletion, onDeleteEntry }) {
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterText, setFilterText] = useState('');
  const [confusionFilter, setConfusionFilter] = useState('all');

  const filteredAndSortedEntries = useMemo(() => {
    let filtered = entries;

    // Apply text filter
    if (filterText) {
      filtered = filtered.filter(entry => 
        entry.taskName.toLowerCase().includes(filterText.toLowerCase()) ||
        entry.notes.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    // Apply confusion filter
    if (confusionFilter !== 'all') {
      const [min, max] = confusionFilter.split('-').map(Number);
      filtered = filtered.filter(entry => {
        const confusionLevel = entry.confusionBefore;
        return confusionLevel >= min && confusionLevel <= max;
      });
    }

    // Sort entries
    return filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      if (sortBy === 'createdAt') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [entries, sortBy, sortOrder, filterText, confusionFilter]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (entries.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="text-center py-16"
      >
        <div className="bg-white rounded-2xl p-12 shadow-sm border border-gray-200 max-w-md mx-auto">
          <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
            <SafeIcon icon={FiInbox} className="text-gray-400 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">No entries yet</h2>
          <p className="text-gray-600 mb-8">
            Start tracking your foundational work progress by creating your first entry.
          </p>
          <Link 
            to="/new-entry" 
            className="inline-flex items-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <SafeIcon icon={FiPlus} className="text-lg" />
            <span>Create First Entry</span>
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="space-y-8"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} tracked
          </p>
        </div>
        
      </div>

      <StatsCards entries={entries} />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Progress Entries</h2>
          <FilterBar 
            filterText={filterText} 
            onFilterTextChange={setFilterText}
            confusionFilter={confusionFilter}
            onConfusionFilterChange={setConfusionFilter}
          />
        </div>
        <EntriesTable 
          entries={filteredAndSortedEntries} 
          sortBy={sortBy} 
          sortOrder={sortOrder} 
          onSort={handleSort}
          onUpdateEntry={onUpdateEntry}
          onUpdateCompletion={onUpdateCompletion}
          onDeleteEntry={onDeleteEntry}
        />
      </div>
    </motion.div>
  );
}

export default Dashboard;